// Small view helpers shared by the Tonk overlay, 3D table and VR panel.

import { useEffect, useState } from 'react';
import type { TableState } from '../gameStore';
import { canHit, spreadKind, SEAT_NAMES, type TonkState, type Spread } from '../../../../supabase/functions/games/tonk.ts';
import { cardLabel } from '../cardFaces';

export const tonkName = (st: TableState | undefined | null, i: number) => st?.seats[i]?.name ?? SEAT_NAMES[i];

/** Is it `seat`'s turn to act (draw or play) right now? */
export const isTurn = (st: TableState | undefined | null, g: TonkState | null | undefined, seat: number) =>
  !!g && st?.phase === 'playing' && (g.phase === 'draw' || g.phase === 'play') && g.turn === seat;

/** Spreads the one selected card can hit. */
export const hitTargets = (g: TonkState, card: string | undefined): Spread[] =>
  card ? g.spreads.filter((sp) => canHit(sp, card)) : [];

export const canSpread = (sel: string[]) => sel.length >= 3 && !!spreadKind(sel);

export const spreadText = (sp: Spread) => sp.cards.map(cardLabel).join(' ');

/** One line describing how the last hand ended. */
export function handLine(st: TableState, g: TonkState, me: number): string {
  const h = g.lastHand;
  if (!h) return '';
  const who = (i: number | null) => (i === null ? '' : i === me ? 'You' : tonkName(st, i));
  const pts = h.winner !== null ? h.points[h.winner] : 0;
  const plus = ` (+${pts})`;
  switch (h.how) {
    case 'tonk': return `${who(h.winner)} dealt a Tonk!${plus}`;
    case 'push': return 'Two Tonks dealt — push, nobody scores';
    case 'out': return `${who(h.winner)} went out${plus}`;
    case 'drop': return `${who(h.dropper)} dropped and won${plus}`;
    case 'caught': return `${who(h.dropper)} dropped — caught by ${h.winner === me ? 'you' : who(h.winner)}${plus}`;
    case 'stock': return `Stock ran out — ${who(h.winner)} had the lowest${plus}`;
  }
  return '';
}

/** Selected cards, kept in step with the hand (cards that left are dropped). */
export function useSelection(hand: string[], resetKey: string) {
  const [sel, setSel] = useState<string[]>([]);
  const handKey = hand.join(',');
  useEffect(() => { setSel((s) => s.filter((c) => hand.includes(c))); }, [handKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setSel([]); }, [resetKey]);
  const toggle = (c: string) => setSel((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));
  return { sel, setSel, toggle };
}
